import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Radio } from 'lucide-react';
import { LIVE_SESSION_DEFAULT_MINUTES, LIVE_SESSION_MAX_MINUTES, SUPPORT_HANDOFF_TTL_SECONDS, type OwnerLiveSessionResultDto } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { OwnerConfirmDialog } from './OwnerConfirm';

const MINUTE_CHOICES = [15, 30, 45, LIVE_SESSION_MAX_MINUTES];

export function LiveSessionDialog({
  tenantId,
  user,
  offline,
  linked,
  onClose,
}: {
  tenantId: string;
  user: { id: string; name: string };
  offline: boolean;
  linked: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState(LIVE_SESSION_DEFAULT_MINUTES);
  const [started, setStarted] = useState<OwnerLiveSessionResultDto | null>(null);

  const finish = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-support-access', tenantId] });
    onClose();
  };

  return (
    <>
      {started && <LiveSessionStartedDialog tenantId={tenantId} started={started} onClose={finish} />}
      {!started && (
        <OwnerConfirmDialog
          open
          onClose={onClose}
          title={`Live session as ${user.name}`}
          description={offline ? "Signs in on the facility's own server, read-only, with no clinical records." : 'Opens the facility in a new tab, read-only, with no clinical records.'}
          warning={
            offline
              ? `A red banner shows on both ends for the whole session, the Super Admins are told, and it is audited here and on the facility server. ${
                  linked
                    ? 'The server is connected to the cloud now and collects the handoff within a minute.'
                    : 'The server is not connected right now, so the handoff reaches it at its next check-in.'
                } At most 60 minutes.`
              : "The facility's Super Admins are told afterwards, and the session appears in their support access log with the reason you give."
          }
          confirmLabel="Start live session"
          successMessage="Session prepared"
          action={(payload) => post<OwnerLiveSessionResultDto>(`/platform/tenants/${tenantId}/live-session`, { ...payload, userId: user.id, minutes })}
          onDone={(result) => setStarted(result)}
        >
          <Field label="Session length">
            <ChoiceButtons
              value={String(minutes)}
              onChange={(value) => setMinutes(Number(value))}
              options={MINUTE_CHOICES.map((choice) => ({ value: String(choice), label: `${choice} minutes` }))}
            />
          </Field>
        </OwnerConfirmDialog>
      )}
    </>
  );
}

function LiveSessionStartedDialog({ tenantId, started, onClose }: { tenantId: string; started: OwnerLiveSessionResultDto; onClose: () => void }) {
  const toast = useToast();
  const [secondsLeft, setSecondsLeft] = useState(SUPPORT_HANDOFF_TTL_SECONDS);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (started.mode !== 'cloud') return undefined;
    const deadline = new Date(started.expiresAt).getTime();
    const timer = window.setInterval(() => {
      const left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) window.clearInterval(timer);
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [started.expiresAt, started.mode]);

  const end = useMutation({
    mutationFn: () => post(`/platform/tenants/${tenantId}/live-session/${started.sessionId}/end`),
    onSuccess: () => {
      toast.success('Live session ended');
      onClose();
    },
    onError: (error) => toast.error('Could not end the session', describeError(error)),
  });

  const openCloud = () => {
    if (!started.handoffPath) return;
    window.open(`${window.location.origin}${started.handoffPath}`, '_blank', 'noopener');
    setOpened(true);
  };

  return (
    <Dialog open onClose={onClose} title={`Live session as ${started.user.name}`} description={`Ends at ${dateTime(started.endsAt)}.`} icon={Radio}>
      <div className="space-y-4 text-sm">
        <div role="status" className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
          <Radio className="h-3.5 w-3.5 flex-none" aria-hidden /> Live session open. The facility sees the same red banner until it ends.
        </div>
        {started.mode === 'cloud' && (
          <>
            {secondsLeft > 0 && !opened && <p>The link works once and expires in {secondsLeft} s.</p>}
            {secondsLeft === 0 && !opened && <Alert variant="warning">The link has expired. Start a new session.</Alert>}
            {opened && <p className="text-muted-foreground">The facility opened in a new tab. Use End session there when you are done.</p>}
          </>
        )}
        {started.mode === 'local' && (
          <>
            <p>
              {started.delivery === 'link'
                ? 'The one-time handoff was sent to the facility server through its live link and arrives within a minute.'
                : 'The one-time handoff is on its way to the facility server with its next check-in.'}{' '}
              The server signs you in by itself and keeps the session for its own audit trail.
            </p>
            {started.linkAvailable ? (
              <p className="text-muted-foreground">Requests of this session are forwarded to the facility server through its link to the cloud: read-only, only for you, and only while the session is open.</p>
            ) : (
              <Alert variant="warning">
                This server is not connected to the cloud right now, so the console cannot forward your screen to it. The link opens by itself as soon as the
                server is online. Until then the session works from inside the facility network only; end it here when you are done.
              </Alert>
            )}
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {started.mode === 'cloud' ? (
            <Button onClick={openCloud} disabled={opened || secondsLeft === 0}>
              Open in a new tab
            </Button>
          ) : (
            <Button variant="destructive" loading={end.isPending} onClick={() => end.mutate()}>
              End session
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
